import { API_URL } from '@/config/api';
/**
 * FounderWelcomeModal - "Why NewAudia Exists"
 * 
 * A personal message from R. Dale Brunet with TTS playback.
 * Features:
 * - NewAudia logo and founder headshot
 * - Full script text (scrollable) with TM superscripts
 * - Audio player (play/pause, progress, restart)
 * - Run-once TTS with R2 durable storage
 * - No autoplay - user-initiated only
 */
import React, { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Play, Pause, RotateCcw, X, Volume2, Mail } from 'lucide-react';

// Founder headshot URL (stored in R2/CDN)
const FOUNDER_HEADSHOT_URL = "https://customer-assets.emergentagent.com/job_17effe54-356c-4367-937e-269458cd2bbe/artifacts/w5osri2b_image.png";

// Helper component for NewAudia with TM
const NewAudiaTM = () => (
  <span className="whitespace-nowrap">NewAudia<sup className="text-[0.6em]">™</sup></span>
);

// The canonical script paragraphs - source of truth
const FOUNDER_SCRIPT_PARAGRAPHS = [
  { text: "Hello, I'm R. Dale Brunet.", hasNewAudia: false },
  { text: "Before you go any further, I want to take just a minute to explain why NewAudia exists—and why I felt compelled to build it.", hasNewAudia: true },
  { text: "For more than a decade, audiobooks—the largest and fastest-growing segment of publishing—have lived inside a closed system. A small number of dominant platforms control access, distribution, and economics. Creators do the work. Readers bring the attention. And most of the value flows upward to the gatekeepers.", hasNewAudia: false },
  { text: "That model didn't break overnight. It hardened quietly—until convenience became control.", hasNewAudia: false },
  { text: "NewAudia exists because I don't believe access to ideas should require surrender. I don't believe creators should trade ownership for visibility. And I don't believe readers were meant to consume books passively, one file at a time.", hasNewAudia: true },
  { text: "This platform isn't about selling audiobooks. It's about restoring engagement.", hasNewAudia: false },
  { text: "Here, books are treated as living conversations. You're invited to explore ideas, ask questions, revisit concepts, and build understanding—not just reach the final chapter.", hasNewAudia: false },
  { text: "Yes, this is a David and Goliath story—but not about fighting size with size. It's about challenging assumptions. The assumption that distribution must be centralized. That innovation only comes from scale. That profit extraction is the natural cost of participation.", hasNewAudia: false },
  { text: "I built NewAudia to prove there's another way.", hasNewAudia: true },
  { text: "If you're here to listen, you're welcome. If you're here to think more deeply, even better.", hasNewAudia: false },
  { text: "Either way—thank you for being part of this experiment.", hasNewAudia: false },
  { text: "Welcome to NewAudia.", hasNewAudia: true }
];

// Render paragraph with NewAudia™ replacements
const renderParagraph = (para) => {
  if (!para.hasNewAudia) {
    return para.text;
  }
  // Split by "NewAudia" and rejoin with the TM component
  const parts = para.text.split('NewAudia');
  return parts.map((part, i) => (
    <React.Fragment key={i}>
      {part}
      {i < parts.length - 1 && <NewAudiaTM />}
    </React.Fragment>
  ));
};

const FounderWelcomeModal = ({ isOpen, onClose }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [audioLoaded, setAudioLoaded] = useState(false);
  const [audioError, setAudioError] = useState(false);
  const [hasTrackedView, setHasTrackedView] = useState(false);
  const audioRef = useRef(null);
  const API = API_URL;

  // Audio URL from R2 storage
  const audioUrl = `${API}/api/founder/welcome-audio`;

  // Track view and auto-play when modal opens
  useEffect(() => {
    if (isOpen && !hasTrackedView) {
      // Track the view
      fetch(`${API}/api/founder/track-view`, { method: 'POST' }).catch(() => {});
      setHasTrackedView(true);
    }
    
    // Reset tracking when modal closes
    if (!isOpen) {
      setHasTrackedView(false);
    }
  }, [isOpen, hasTrackedView, API]);

  // Auto-play when audio is loaded and modal is open
  useEffect(() => {
    if (isOpen && audioLoaded && audioRef.current && !isPlaying) {
      // Small delay to let animations complete
      const timer = setTimeout(() => {
        audioRef.current.play()
          .then(() => setIsPlaying(true))
          .catch(err => console.log('Autoplay prevented:', err));
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isOpen, audioLoaded]);

  // Reset state when modal closes
  const handleClose = () => {
    setIsPlaying(false);
    setCurrentTime(0);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    onClose();
  };

  // Handle audio events
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
      setAudioLoaded(true);
      setAudioError(false);
    };
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      audio.currentTime = 0;
    };
    const handleError = () => {
      setAudioError(true);
      setAudioLoaded(false);
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
    };
  }, []);

  const togglePlay = async () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      try {
        await audio.play();
        setIsPlaying(true);
      } catch (err) {
        console.error('Playback error:', err);
      }
    }
  };

  const handleRestart = () => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = 0;
    setCurrentTime(0);
    if (isPlaying) {
      audio.play();
    }
  };

  const handleSeek = (e) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    audio.currentTime = percentage * duration;
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] sm:max-h-[85vh] overflow-hidden bg-gradient-to-br from-slate-900 via-slate-900 to-slate-950 border border-slate-700/50 p-0">
        {/* Visually hidden title for accessibility */}
        <div className="sr-only" role="heading" aria-level="1">Why NewAudia Exists - A message from founder R. Dale Brunet</div>
        
        {/* Hidden audio element */}
        <audio ref={audioRef} src={audioUrl} preload="metadata" />

        {/* Header with logo and close button - Mobile optimized */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-3 sm:px-6 py-3 sm:py-4 bg-slate-900/95 backdrop-blur-sm border-b border-slate-800">
          <div className="flex items-center gap-2 sm:gap-4">
            {/* NewAudia Logo */}
            <img 
              src="/newaudia-logo.jpg" 
              alt="NewAudia" 
              className="h-8 sm:h-10 w-auto rounded"
            />
            <div className="h-5 sm:h-6 w-px bg-slate-700" />
            <h2 className="text-sm sm:text-lg font-semibold text-white">Why <NewAudiaTM /> Exists</h2>
          </div>
          <button
            onClick={handleClose}
            className="p-1.5 sm:p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition-colors"
            aria-label="Close"
          >
            <X className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        </div>

        {/* Content - Mobile optimized */}
        <div className="overflow-y-auto max-h-[calc(90vh-160px)] sm:max-h-[calc(85vh-180px)]">
          {/* Founder Header */}
          <div className="flex flex-col items-center gap-4 sm:gap-6 px-4 sm:px-6 py-4 sm:py-6 bg-gradient-to-r from-purple-900/30 to-indigo-900/30 border-b border-slate-800">
            {/* Headshot - Centered on mobile */}
            <div className="w-20 h-20 sm:w-28 sm:h-28 rounded-full overflow-hidden border-2 border-purple-500/50 flex-shrink-0 shadow-xl shadow-purple-500/20">
              <img
                src={FOUNDER_HEADSHOT_URL}
                alt="R. Dale Brunet"
                className="w-full h-full object-cover"
              />
            </div>
            
            {/* Founder Info - Centered on mobile */}
            <div className="text-center">
              <h3 className="text-lg sm:text-2xl font-bold text-white">R. Dale Brunet</h3>
              <p className="text-xs sm:text-sm text-purple-300 uppercase tracking-wider mt-1">
                Founder, NewAudia™
              </p>
              <p className="text-slate-400 text-xs sm:text-sm mt-2 max-w-md">
                A personal message about why this platform exists and what it means for readers and creators.
              </p>
            </div>
          </div>

          {/* Script Text with staggered paragraph animations */}
          <div className="px-6 py-6">
            <div className="prose prose-invert prose-sm max-w-none">
              {FOUNDER_SCRIPT_PARAGRAPHS.map((para, index) => (
                <p 
                  key={index} 
                  className="text-slate-300 text-sm sm:text-base leading-relaxed mb-3 sm:mb-4 last:mb-0"
                >
                  {renderParagraph(para)}
                </p>
              ))}
            </div>

            {/* Signature */}
            <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-slate-700/50">
              <p className="text-white font-semibold text-sm sm:text-base">R. Dale Brunet</p>
              <p className="text-purple-300 text-xs sm:text-sm">Founder, <NewAudiaTM /></p>
              
              {/* Email Link */}
              <a 
                href="mailto:info@NewAudia.com"
                className="inline-flex items-center gap-2 mt-3 sm:mt-4 text-xs sm:text-sm text-slate-400 hover:text-purple-300 transition-colors"
              >
                <Mail className="w-3 h-3 sm:w-4 sm:h-4" />
                info@NewAudia.com
              </a>
            </div>
          </div>
        </div>

        {/* Audio Player Footer - Mobile optimized */}
        <div className="sticky bottom-0 px-3 sm:px-6 py-3 sm:py-4 bg-slate-900/95 backdrop-blur-sm border-t border-slate-800">
          {/* Progress Bar */}
          <div 
            className="w-full h-1.5 sm:h-2 bg-slate-700 rounded-full cursor-pointer mb-2 sm:mb-3 overflow-hidden"
            onClick={handleSeek}
          >
            <div 
              className="h-full bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full transition-all duration-150"
              style={{ width: `${progress}%` }}
            />
          </div>

          {/* Controls */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              {/* Play/Pause Button */}
              <Button
                onClick={togglePlay}
                disabled={audioError && !audioLoaded}
                className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white shadow-lg shadow-purple-500/25 transition-all"
              >
                {isPlaying ? (
                  <Pause className="w-4 h-4 sm:w-5 sm:h-5" />
                ) : (
                  <Play className="w-4 h-4 sm:w-5 sm:h-5 ml-0.5" />
                )}
              </Button>

              {/* Restart Button */}
              <button
                onClick={handleRestart}
                className="p-1.5 sm:p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition-colors"
                title="Restart"
              >
                <RotateCcw className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>

              {/* Time Display */}
              <span className="text-xs sm:text-sm text-slate-400 font-mono">
                {formatTime(currentTime)} / {formatTime(duration) || '~1:30'}
              </span>
            </div>

            {/* Status */}
            <div className="text-[10px] sm:text-xs text-slate-500">
              {audioError ? (
                <span className="text-amber-400">Audio loading...</span>
              ) : audioLoaded ? (
                <span className="text-emerald-400 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-emerald-400 rounded-full animate-pulse"></span>
                  <span className="hidden sm:inline">Ready to play</span>
                  <span className="sm:hidden">Ready</span>
                </span>
              ) : (
                <span className="flex items-center gap-1">
                  <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-slate-400 rounded-full animate-pulse"></span>
                  <span className="hidden sm:inline">Loading audio...</span>
                  <span className="sm:hidden">Loading...</span>
                </span>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FounderWelcomeModal;
